import React from "react";
import { FiAlertTriangle, FiCheck } from "react-icons/fi";

/** Presentation pieces for the guest reward-claim flow (guestUserCompleteUi). */

const STEPS = [
  { value: 1, label: "Email" },
  { value: 1.5, label: "Code" },
  { value: 2, label: "Name" },
  { value: 3, label: "Password" },
];

/** Read-only progress indicator; `step` is owned by the parent. */
export const GuestStepper = ({ step }: { step: number }) => {
  const current = Math.max(0, STEPS.findIndex((item) => item.value === step));
  return (
    <ol className="mb-7 flex items-center" aria-label="Progress">
      {STEPS.map((item, index) => {
        const done = index < current;
        const active = index === current;
        return (
          <li key={item.value} className={`flex items-center ${index < STEPS.length - 1 ? "flex-1" : ""}`}>
            <div className="flex flex-col items-center gap-1.5">
              <span
                aria-current={active ? "step" : undefined}
                className={`inline-flex h-8 w-8 items-center justify-center rounded-full border text-xs font-semibold transition-colors ${
                  done
                    ? "border-primary bg-primary text-ink-inverse"
                    : active
                    ? "border-primary bg-primary-soft text-primary-text"
                    : "border-line-strong bg-surface text-ink-4"
                }`}
              >
                {done ? <FiCheck className="h-4 w-4" aria-hidden="true" /> : index + 1}
              </span>
              <span className={`text-[11px] font-medium ${active ? "text-ink" : "text-ink-4"}`}>{item.label}</span>
            </div>
            {index < STEPS.length - 1 && (
              <span className={`mx-2 mb-5 h-px flex-1 ${done ? "bg-primary" : "bg-line"}`} aria-hidden="true" />
            )}
          </li>
        );
      })}
    </ol>
  );
};

const BENEFITS = [
  "Securely claim and track your rewards",
  "Access exclusive paid surveys",
  "Receive gift cards and payout notifications",
  "Save your survey history and earnings",
];

export const VerifyBenefits = () => (
  <div className="mb-6 rounded-lg border border-line bg-surface-2 p-4">
    <h3 className="mb-3 text-sm font-semibold text-ink">Why Verify Your Email?</h3>
    <ul className="space-y-2 text-sm text-ink-2">
      {BENEFITS.map((benefit) => (
        <li key={benefit} className="flex items-start gap-2.5">
          <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-success-soft text-success">
            <FiCheck className="h-2.5 w-2.5" aria-hidden="true" />
          </span>
          {benefit}
        </li>
      ))}
    </ul>
  </div>
);

/** Error dialog chrome. The parent owns visibility and passes its own close handler. */
export const GuestErrorModal = ({ message, onClose }: { message: React.ReactNode; onClose: () => void }) => (
  <div className="zs-backdrop" role="alertdialog" aria-modal="true">
    <div className="zs-modal max-w-sm p-6 text-center">
      <span className="mx-auto inline-flex h-11 w-11 items-center justify-center rounded-full bg-danger-soft text-danger">
        <FiAlertTriangle className="h-5 w-5" aria-hidden="true" />
      </span>
      <h2 className="mt-4 font-display text-lg font-bold text-ink">Error</h2>
      <p className="mt-2 mb-6 text-sm text-ink-3">{message}</p>
      <button onClick={onClose} className="zs-btn zs-btn-danger zs-btn-block">Close</button>
    </div>
  </div>
);
